/**
 * AVALIAÇÃO DE ENGENHARIA do Relatório de Integridade: condição macro do
 * equipamento (com resultado derivado, nunca digitado) e Vida Residual em
 * meses — do AVR quando existe, informada pelo Engenheiro quando não existe,
 * sempre limitada a 240 meses na adoção, sem perder o valor original.
 */

export type EquipmentCondition = "APTO" | "NAO_APTO" | "CRITERIO_CLIENTE";

/** Rótulos exatamente como o bloco pede. */
export const EQUIPMENT_CONDITION_LABELS: Record<EquipmentCondition, string> = {
  APTO: "APTO",
  NAO_APTO: "NÃO APTO",
  CRITERIO_CLIENTE: "A CRITÉRIO DO CLIENTE"
};

export type EquipmentResult = "APROVADO" | "REPROVADO" | "N/A";

/** APTO → APROVADO · NÃO APTO → REPROVADO · A CRITÉRIO DO CLIENTE → N/A. Derivado, somente leitura. */
export function deriveEquipmentResult(condition: EquipmentCondition | null | undefined): EquipmentResult | null {
  switch (condition) {
    case "APTO": return "APROVADO";
    case "NAO_APTO": return "REPROVADO";
    case "CRITERIO_CLIENTE": return "N/A";
    default: return null;
  }
}

export function isEquipmentCondition(value: unknown): value is EquipmentCondition {
  return value === "APTO" || value === "NAO_APTO" || value === "CRITERIO_CLIENTE";
}

export const EQUIPMENT_CONDITION_REQUIRED = "Selecione a Condição do Equipamento.";

export type ResidualLifeOrigin = "AVR" | "ENGINEER";

export const RESIDUAL_LIFE_ORIGIN_LABELS: Record<ResidualLifeOrigin, string> = {
  AVR: "AVR",
  ENGINEER: "INFORMADA PELO ENGENHEIRO"
};

/** Limite máximo adotado na análise/Minuta. */
export const RESIDUAL_LIFE_MAX_MONTHS = 240;
export const RESIDUAL_LIFE_MIN_MONTHS = 1;
export const RESIDUAL_LIFE_RANGE_INVALID = "A Vida Residual deve estar entre 1 e 240 meses.";

/**
 * Anos (valor técnico persistido no AVR) → meses inteiros. Arredonda para
 * baixo: 8,0 anos → 96; 12,58 anos → 150. Nunca arredonda para cima uma vida
 * remanescente.
 */
export function yearsToMonths(years: number | null | undefined): number | null {
  if (years === null || years === undefined || !Number.isFinite(years) || years < 0) return null;
  return Math.floor(years * 12 + 1e-9);
}

/** 96 → 96 · 180 → 180 · 240 → 240 · 300 → 240 · 600 → 240. */
export function adoptResidualLifeMonths(calculatedMonths: number | null | undefined): number | null {
  if (calculatedMonths === null || calculatedMonths === undefined || !Number.isFinite(calculatedMonths)) return null;
  // Abaixo de 1 mês o AVR está dizendo "vida esgotada": adota 0, nunca inventa 1.
  return Math.min(RESIDUAL_LIFE_MAX_MONTHS, Math.max(0, Math.floor(calculatedMonths)));
}

/** Entrada manual: só inteiros de 1 a 240. */
export function validateManualResidualLife(value: number | string | null | undefined): string[] {
  const n = typeof value === "string" ? Number(value.replace(",", ".").trim()) : value;
  if (n === null || n === undefined || value === "" || !Number.isFinite(n as number)) return [RESIDUAL_LIFE_RANGE_INVALID];
  if (!Number.isInteger(n)) return [RESIDUAL_LIFE_RANGE_INVALID];
  if ((n as number) < RESIDUAL_LIFE_MIN_MONTHS || (n as number) > RESIDUAL_LIFE_MAX_MONTHS) return [RESIDUAL_LIFE_RANGE_INVALID];
  return [];
}

export interface AvrResidualLifeSource {
  avrId: string;
  avrNumber: string;
  revision: string;
  /** Data de referência do AVR (assinatura, aprovação ou cálculo). */
  date: string;
  /** Vida remanescente do ponto crítico, em anos, como persistida no AVR. */
  remainingLifeYears: number;
}

export interface ResidualLifeAssessment {
  origin: ResidualLifeOrigin;
  /** Meses calculados pelo AVR (convertidos), preservados mesmo acima de 240. Nulo quando manual. */
  calculatedMonths: number | null;
  /** O que a análise/Minuta usa — nunca acima de 240. */
  adoptedMonths: number;
  capped: boolean;
  source: AvrResidualLifeSource | null;
  setAt: string;
  setBy: string;
}

/** Vida Residual a partir de um AVR válido: converte, limita a 240 e preserva o original. */
export function residualLifeFromAvr(source: AvrResidualLifeSource, at: string, by: string): ResidualLifeAssessment {
  const calculated = yearsToMonths(source.remainingLifeYears);
  const adopted = adoptResidualLifeMonths(calculated);
  return {
    origin: "AVR",
    calculatedMonths: calculated,
    adoptedMonths: adopted ?? 0,
    capped: calculated !== null && adopted !== null && calculated > adopted,
    source,
    setAt: at,
    setBy: by
  };
}

/** Vida Residual informada pelo Engenheiro (sem AVR): validada de 1 a 240. */
export function residualLifeFromEngineer(months: number | string, at: string, by: string): ResidualLifeAssessment {
  const errors = validateManualResidualLife(months);
  if (errors.length > 0) throw new RangeError(errors[0]);
  const n = typeof months === "string" ? Number(months.replace(",", ".").trim()) : months;
  return { origin: "ENGINEER", calculatedMonths: null, adoptedMonths: n, capped: false, source: null, setAt: at, setBy: by };
}

export interface EngineeringAssessment {
  equipmentCondition: EquipmentCondition | null;
  /** Derivado da condição — nunca gravado por fora. */
  equipmentResult: EquipmentResult | null;
  residualLife: ResidualLifeAssessment | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export function emptyEngineeringAssessment(): EngineeringAssessment {
  return { equipmentCondition: null, equipmentResult: null, residualLife: null, updatedAt: null, updatedBy: null };
}

/** Garante a consistência condição → resultado ao ler qualquer registro persistido. */
export function normalizeEngineeringAssessment(value: Partial<EngineeringAssessment> | null | undefined): EngineeringAssessment {
  const condition = isEquipmentCondition(value?.equipmentCondition) ? value!.equipmentCondition! : null;
  return {
    equipmentCondition: condition,
    equipmentResult: deriveEquipmentResult(condition),
    residualLife: value?.residualLife ?? null,
    updatedAt: value?.updatedAt ?? null,
    updatedBy: value?.updatedBy ?? null
  };
}

export function formatResidualLifeMonths(months: number | null | undefined): string {
  if (months === null || months === undefined || !Number.isFinite(months)) return "—";
  return `${months} ${months === 1 ? "mês" : "meses"}`;
}
